import { supabase } from './supabase';
import { withRetry } from './retry';
import { cacheService } from './cache';

type MetricName = 'FCP' | 'LCP' | 'CLS' | 'FID' | 'TTFB';

interface PerformanceMetric {
  name: MetricName;
  value: number;
  path: string;
  timestamp: number;
}

class MetricsService {
  private metrics: PerformanceMetric[] = [];
  private flushInterval: number = 10000; // 10 segundos
  private maxBatchSize: number = 10;
  private timer: NodeJS.Timeout | null = null;
  private isInitialized: boolean = false;

  constructor() {
    this.initialize();
  }

  private async initialize() {
    if (this.isInitialized) return;
    
    try {
      // Verifica se a tabela existe
      const { error } = await supabase
        .from('metrics')
        .select('id')
        .limit(1);

      if (!error) {
        this.isInitialized = true;
        this.startTimer();
      } else {
        console.warn('Tabela de métricas não encontrada:', error);
      }
    } catch (err) {
      console.warn('Erro ao inicializar serviço de métricas:', err);
    }
  }

  private startTimer() {
    if (this.timer) return;
    
    this.timer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  async track(metric: PerformanceMetric) {
    if (!this.isInitialized) {
      console.warn('Serviço de métricas não inicializado');
      return;
    }

    this.metrics.push(metric);

    if (this.metrics.length >= this.maxBatchSize) {
      await this.flush();
    }
  }

  private formatTimestamp(timestamp: number): string {
    // Converte o timestamp para um objeto Date
    const date = new Date(timestamp);
    // Retorna a data em formato ISO sem milissegundos
    return date.toISOString().split('.')[0] + 'Z';
  }

  private async flush() {
    if (this.metrics.length === 0) return;

    const metricsToSend = [...this.metrics];
    this.metrics = [];

    try {
      await withRetry(async () => {
        // Busca o usuário atual de forma assíncrona
        const { data: { user } } = await supabase.auth.getUser();
        
        // Prepara os dados com o ID do usuário se disponível e formata o timestamp
        const metricsData = metricsToSend.map(metric => ({
          name: metric.name,
          value: metric.value,
          path: metric.path,
          timestamp: this.formatTimestamp(metric.timestamp),
          user_id: user?.id || null,
          browser: navigator.userAgent,
          device_type: this.getDeviceType(),
          connection_type: (navigator as any).connection?.effectiveType || 'unknown'
        }));

        // Insere as métricas
        const { error } = await supabase
          .from('metrics')
          .insert(metricsData);

        if (error) {
          console.error('Erro ao inserir métricas:', error);
          throw error;
        }
      }, {
        attempts: 3,
        delay: 1000,
        backoff: true
      });
    } catch (err) {
      console.error('Erro ao enviar métricas:', err);
      // Readiciona as métricas que falharam
      this.metrics = [...metricsToSend, ...this.metrics];
    }
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  startTracking() {
    if (!this.isInitialized) {
      this.initialize();
    }

    // Importa web-vitals dinamicamente apenas quando necessário
    import('web-vitals').then(({ onCLS, onFCP, onFID, onLCP, onTTFB }) => {
      // Monitora First Contentful Paint
      onFCP(({ name, value }) => {
        this.track({
          name: name as MetricName,
          value: Math.round(value),
          path: window.location.pathname,
          timestamp: Date.now()
        });
      });

      // Monitora Largest Contentful Paint
      onLCP(({ name, value }) => {
        this.track({
          name: name as MetricName,
          value: Math.round(value),
          path: window.location.pathname,
          timestamp: Date.now()
        });
      });

      // Monitora First Input Delay
      onFID(({ name, value }) => {
        this.track({
          name: name as MetricName,
          value: Math.round(value),
          path: window.location.pathname,
          timestamp: Date.now()
        });
      });

      // Monitora Cumulative Layout Shift
      onCLS(({ name, value }) => {
        this.track({
          name: name as MetricName,
          value: Math.round(value * 1000), // Converte para milissegundos
          path: window.location.pathname,
          timestamp: Date.now()
        });
      });

      // Monitora Time to First Byte
      onTTFB(({ name, value }) => {
        this.track({
          name: name as MetricName,
          value: Math.round(value),
          path: window.location.pathname,
          timestamp: Date.now()
        });
      });
    }).catch(err => {
      console.warn('Não foi possível carregar web-vitals:', err);
    });
  }

  cleanup() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.metrics = [];
    this.isInitialized = false;
  }
}

export const metricsService = new MetricsService();